import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ExternalLink, Instagram } from "lucide-react";
import { getSettings } from "@/lib/api";

const ALLOWED_SCRIPT_HOSTS = ["elfsightcdn.com"];

function parseEmbedCode(embedCode) {
  if (!embedCode) {
    return {
      html: "",
      scripts: [],
    };
  }

  const wrapper = document.createElement("div");
  wrapper.innerHTML = embedCode;

  const scriptNodes = Array.from(wrapper.querySelectorAll("script"));

  const scripts = scriptNodes
    .map((script) => ({
      src: script.getAttribute("src") || "",
      async: script.hasAttribute("async"),
      defer: script.hasAttribute("defer"),
      type: script.getAttribute("type") || "",
    }))
    .filter((script) => script.src);

  scriptNodes.forEach((script) => script.remove());

  return {
    html: wrapper.innerHTML,
    scripts,
  };
}

function isAllowedScript(src) {
  try {
    const url = new URL(src);
    return ALLOWED_SCRIPT_HOSTS.some((host) => url.hostname === host || url.hostname.endsWith(`.${host}`));
  } catch {
    return false;
  }
}

function ScriptedEmbed({ embedCode }) {
  const parsedEmbed = useMemo(() => {
    if (typeof document === "undefined") {
      return {
        html: "",
        scripts: [],
      };
    }

    return parseEmbedCode(embedCode);
  }, [embedCode]);

  useEffect(() => {
    if (!parsedEmbed.scripts.length) return;

    parsedEmbed.scripts.forEach((scriptConfig) => {
      if (!isAllowedScript(scriptConfig.src)) {
        console.warn("Script de embed bloqueado por seguridad:", scriptConfig.src);
        return;
      }

      const existingScripts = document.querySelectorAll(
        `script[src="${scriptConfig.src}"]`
      );

      existingScripts.forEach((script) => {
        script.parentNode?.removeChild(script);
      });

      const script = document.createElement("script");
      script.src = scriptConfig.src;
      script.async = scriptConfig.async || true;

      if (scriptConfig.defer) {
        script.defer = true;
      }

      if (scriptConfig.type) {
        script.type = scriptConfig.type;
      }

      script.setAttribute("data-managed-embed", "true");
      document.body.appendChild(script);
    });

    return () => {
      document
        .querySelectorAll('script[data-managed-embed="true"]')
        .forEach((script) => {
          script.parentNode?.removeChild(script);
        });
    };
  }, [parsedEmbed.scripts]);

  if (!parsedEmbed.html) return null;

  return (
    <div
      className="w-full overflow-hidden"
      dangerouslySetInnerHTML={{ __html: parsedEmbed.html }}
    />
  );
}

export default function InstagramPage() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getSettings()
      .then((response) => {
        setSettings(response.data || {});
      })
      .catch(() => {
        setSettings({});
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const instagramUrl = settings.instagram_url || "";
  const instagramEmbed = settings.instagram_embed || "";

  return (
    <main data-testid="instagram-page" className="pt-20 lg:pt-24 bg-construtop-texture">
      <section className="bg-zinc-950 py-20 lg:py-24 section-padding">
        <div className="max-w-7xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-lime-400 text-xs font-bold uppercase tracking-[0.28em] mb-4"
          >
            NOVEDADES
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight"
          >
            NUESTRO CONTENIDO
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-sm sm:text-base text-zinc-400 mt-5 max-w-3xl"
          >
            Conoce nuestros proyectos, consejos técnicos y novedades en redes sociales.
          </motion.p>
        </div>
      </section>

      <section className="py-16 lg:py-20 px-4 sm:px-8">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="bg-white border border-zinc-200 p-10 text-center">
              <p className="text-sm text-zinc-500">Cargando novedades...</p>
            </div>
          ) : instagramEmbed ? (
            <div className="bg-white/70 border border-zinc-200 p-4 sm:p-6 lg:p-8 overflow-hidden">
              <ScriptedEmbed embedCode={instagramEmbed} />
            </div>
          ) : instagramUrl ? (
            <div className="bg-white border border-zinc-200 p-10 text-center">
              <Instagram size={42} className="mx-auto text-emerald-700 mb-4" />

              <h2 className="font-heading text-2xl text-zinc-900 mb-3">
                Síguenos en Instagram
              </h2>

              <p className="text-sm text-zinc-500 mb-6">
                Visita nuestro perfil para ver las últimas publicaciones de Construtop.
              </p>

              <a
                href={instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white px-6 py-4 text-xs uppercase tracking-[0.18em] font-bold transition-colors"
              >
                Ver Instagram
                <ExternalLink size={14} />
              </a>
            </div>
          ) : (
            <div className="bg-white border border-zinc-200 p-10 text-center">
              <Instagram size={42} className="mx-auto text-emerald-700 mb-4" />

              <h2 className="font-heading text-2xl text-zinc-900 mb-3">
                Próximamente
              </h2>

              <p className="text-sm text-zinc-500">
                Próximamente compartiremos novedades aquí.
              </p>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}